/**
 * OneDesk 360 · Finance calculation helpers (v2 pricing model).
 *
 * A sale's `pricing_version` field determines which formula applies:
 *   • v2 (Feb-2026 rollout) → new formula with Dealer Disbursement Amount (DDA)
 *   • v1 / missing (legacy) → old down_payment-based formula
 *
 * Every new sale from Feb-2026 onwards is created with pricing_version="v2"
 * by the backend. Older records keep the v1 shape, so all display surfaces
 * must delegate through `computeFinanceSummary()` and branch on `isV2`.
 */

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function applyDiscount(value, disc) {
  if (!disc || typeof disc !== "object") return value;
  const v = num(disc.value);
  if (v <= 0) return value;
  const mode = (disc.mode || "abs").toLowerCase();
  if (mode === "pct") return Math.max(value - value * (v / 100), 0);
  return Math.max(value - v, 0);
}

export const PRICING_V2 = "v2";

/** Return true when the sale has been stamped with the v2 pricing model. */
export function isV2Sale(sale) {
  return sale?.pricing_version === PRICING_V2;
}

/** Return true when the sale's buy_mode is "finance". */
export function isFinanceSale(sale) {
  return (sale?.finance?.buy_mode || "").toLowerCase() === "finance";
}

/**
 * Compute the price breakup for the given sale. Always returns a well-typed
 * object so consumers can render unconditionally; `isV2` tells them which
 * layout to show.
 *
 * Returns:
 *   {
 *     isV2, isFinance,
 *     lines: { exshowroom, registration, hypothecation, insurance,
 *              pp, amc, rsa, admin_incidental, accessories },
 *     gross, accessories, netOnRoad, dda, receivable,
 *   }
 */
export function computeFinanceSummary(sale) {
  const payment = sale?.payment || {};
  const discounts = payment.discounts || {};
  const vas = sale?.vas || {};

  // VAS opt-out gate — when the customer has explicitly opted out (`*_opted`
  // is strictly false), the corresponding payment row is forced to 0 in every
  // total. Legacy sales with null/undefined stay opted-in for compat.
  const vasBlocked = {
    pp:  vas.pp_opted  === false,
    amc: vas.amc_opted === false,
    rsa: vas.rsa_opted === false,
  };

  const nettedLine = (k) => {
    if (vasBlocked[k]) return 0;
    return applyDiscount(num(payment[k]), discounts[k]);
  };
  const lines = {
    exshowroom:       nettedLine("exshowroom"),
    registration:     nettedLine("registration"),
    hypothecation:    nettedLine("hypothecation"),
    insurance:        nettedLine("insurance"),
    pp:               nettedLine("pp"),
    amc:              nettedLine("amc"),
    rsa:              nettedLine("rsa"),
    admin_incidental: nettedLine("admin_incidental"),
    accessories:      nettedLine("accessories"),
  };

  const isV2 = isV2Sale(sale);
  const isFinance = isFinanceSale(sale);

  if (isV2) {
    const grossKeys = ["exshowroom", "registration", "hypothecation", "insurance",
                       "pp", "amc", "rsa", "admin_incidental"];
    const gross = round2(grossKeys.reduce((a, k) => a + lines[k], 0));
    const accessories = round2(lines.accessories);
    const netOnRoad = round2(gross + accessories);
    // Overall discount (payment.discounts.total) is applied AFTER Net On-Road
    // per the Feb-2026 spec addendum, so it reduces "To Receive from Customer"
    // directly (not the Gross figure).
    const discAfter = round2(netOnRoad - applyDiscount(netOnRoad, discounts.total));
    const dda = isFinance ? num(sale?.finance?.dealer_disbursement_amount) : 0;
    const receivable = round2(Math.max(netOnRoad - discAfter - dda, 0));
    return {
      isV2: true, isFinance, lines,
      gross, accessories, netOnRoad, dda, receivable,
      discount: discAfter,
      discountConfig: discounts.total || null,
    };
  }

  // v1 legacy path (kept intact for older sales).
  const dp = num(payment.down_payment);
  const dpa = num(payment.dp_accessories);
  let receivable;
  if (isFinance) {
    receivable = round2(dp + dpa);
  } else {
    const keys = ["exshowroom", "registration", "hypothecation", "insurance",
                  "pp", "amc", "rsa", "accessories", "admin_incidental"];
    let total = keys.reduce((a, k) => a + lines[k], 0);
    total = applyDiscount(total, discounts.total);
    receivable = round2(Math.max(total, 0));
  }
  return { isV2: false, isFinance, lines, gross: 0, accessories: lines.accessories,
           netOnRoad: 0, dda: 0, receivable };
}

function round2(n) { return Math.round(n * 100) / 100; }
